
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScryfallCard } from '@/types/scryfall';
import { cn } from '@/lib/utils';
import { useState, useRef, MouseEvent, TouchEvent } from 'react';

interface MTGCardProps {
  card: ScryfallCard;
  count?: number;
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode;
  imageClassName?: string;
  showCount?: boolean;
  enable3D?: boolean;
  highlight?: boolean;
}

export function MTGCard({
  card,
  count,
  onClick,
  className,
  children,
  imageClassName,
  showCount = true,
  enable3D = true,
  highlight = false
}: MTGCardProps) {
  const [transform, setTransform] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!enable3D || !cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const maxRotation = card.is_foil === 'foil' ? 12 : 10; // Slightly more rotation for foil cards
    const rotateX = (y - centerY) / centerY * -maxRotation;
    const rotateY = (x - centerX) / centerX * maxRotation;
    const scale = card.is_foil === 'foil' ? 1.03 : 1.02; // Slightly more scale for foil cards

    setTransform(`perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(${scale}, ${scale}, ${scale})`);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (!enable3D) return;
    setTransform('perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)');
    setIsHovered(false);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!enable3D || !cardRef.current || e.touches.length === 0) return;

    const rect = cardRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const maxRotation = card.is_foil === 'foil' ? 12 : 10;
    const rotateX = (y - centerY) / centerY * -maxRotation;
    const rotateY = (x - centerX) / centerX * maxRotation;
    const scale = card.is_foil === 'foil' ? 1.03 : 1.02;

    setTransform(`perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(${scale}, ${scale}, ${scale})`);
    setIsHovered(true);
  };

  const handleTouchEnd = () => {
    if (!enable3D) return;
    setTransform('perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)');
    setIsHovered(false);
  };

  return (
    <Card
      ref={cardRef}
      className={cn(
        "overflow-hidden relative",
        enable3D ? "transition-all duration-150 ease-out" : "transition-transform duration-200 ease-out",
        onClick && "cursor-pointer",
        enable3D && isHovered && "shadow-2xl shadow-black/25",
        highlight && "ring-2 ring-offset-2 ring-primary",
        className
      )}
      style={{ transform }}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <img
        src={card.card_faces?.[0].image_uris?.normal || card.image_uris?.normal || card.image_uris?.small}
        alt={card.name}
        className={cn("w-full", imageClassName)}
      />

      {/* Language badge */}
      {card.lang && card.lang !== 'en' && (
        <Badge
          variant="outline"
          className="absolute top-1 right-1 border-transparent bg-primary text-primary-foreground"
        >
          {card.lang.toUpperCase()}
        </Badge>
      )}

      {/* Count badge */}
      {showCount && count && count > 1 && (
        <div
          className="absolute top-[12%] left-[9%] w-9 h-9 px-5 flex items-center justify-center bg-background/80 rounded-md"
        >
          <span className="text-xs font-bold text-white">x{count}</span>
        </div>
      )}

      {/* Static foil effect when not hovered */}
      {card.is_foil === 'foil' && enable3D && !isHovered && (
        <>
          {/* Static rainbow holographic base */}
          <div className="absolute inset-0 opacity-25 pointer-events-none"
               style={{
                 background: 'linear-gradient(45deg, #ff0080, #ff8c00, #40e0d0, #9400d3, #ff1493, #00ff7f, #1e90ff, #ff69b4, #ff0080)',
                 mixBlendMode: 'overlay'
               }} />

          {/* Static vibrant color layers */}
          <div className="absolute inset-0 bg-gradient-to-tr from-cyan-300/15 via-violet-400/20 to-fuchsia-300/15 opacity-25 pointer-events-none"
               style={{ mixBlendMode: 'color-dodge' }} />
        </>
      )}

      {/* Animated foil shimmer effect on hover */}
      {card.is_foil === 'foil' && enable3D && isHovered && (
        <>
          {/* Rainbow holographic base with rotation */}
          <div className="absolute inset-0 opacity-30 pointer-events-none animate-rainbow-rotate"
               style={{
                 background: 'linear-gradient(45deg, #ff0080 0%, #ff8c00 12.5%, #40e0d0 25%, #9400d3 37.5%, #ff1493 50%, #00ff7f 62.5%, #1e90ff 75%, #ff69b4 87.5%, #ff0080 100%)',
                 mixBlendMode: 'overlay'
               }} />

          {/* Animated rainbow conic gradient */}
          <div className="absolute inset-0 opacity-25 pointer-events-none animate-rainbow-rotate"
               style={{
                 background: 'conic-gradient(from 0deg, #ff006e, #fb5607, #ffbe0b, #8338ec, #3a86ff, #06ffa5, #ff006e)',
                 animationDuration: '4s',
                 animationDirection: 'reverse',
                 mixBlendMode: 'soft-light'
               }} />

          {/* Vibrant color layers */}
          <div className="absolute inset-0 bg-gradient-to-tr from-cyan-300/20 via-violet-400/25 to-fuchsia-300/20 opacity-35 pointer-events-none animate-pulse"
               style={{ mixBlendMode: 'color-dodge' }} />
          <div className="absolute inset-0 bg-gradient-to-bl from-emerald-300/15 via-yellow-300/20 to-red-300/15 opacity-30 pointer-events-none animate-pulse"
               style={{ animationDelay: '1s', mixBlendMode: 'overlay' }} />
        </>
      )}



      {/* Additional content */}
      {children}
    </Card>
  );
}